import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, CalendarWeek, BlogStageInfo, ContentItem } from '../lib/api';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  PencilSquareIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PlayIcon,
  EyeIcon,
  SparklesIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

// Season styling
const SEASON_STYLES: Record<string, { bg: string; border: string; text: string; label: string; accent: string }> = {
  winter: { bg: 'bg-blue-50', border: 'border-blue-400', text: 'text-blue-800', label: 'Winter', accent: 'from-blue-400 to-blue-500' },
  spring: { bg: 'bg-green-50', border: 'border-green-400', text: 'text-sage-800', label: 'Spring', accent: 'from-sage-400 to-sage-500' },
  summer: { bg: 'bg-amber-50', border: 'border-amber-400', text: 'text-amber-800', label: 'Summer', accent: 'from-amber-400 to-amber-500' },
  fall:   { bg: 'bg-orange-50', border: 'border-orange-400', text: 'text-orange-800', label: 'Fall', accent: 'from-copper-400 to-copper-500' },
};

const TYPE_STYLES: Record<string, { bg: string; text: string }> = {
  'how-to':     { bg: 'bg-blue-100', text: 'text-blue-800' },
  'guide':      { bg: 'bg-sage-100', text: 'text-sage-800' },
  'listicle':   { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  'case-study': { bg: 'bg-pink-100', text: 'text-pink-800' },
  'faq':        { bg: 'bg-indigo-100', text: 'text-indigo-800' },
  'comparison': { bg: 'bg-purple-100', text: 'text-purple-800' },
};

const MONTH_ORDER = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export default function BlogCalendarReviewPage() {
  const navigate = useNavigate();
  const [stage, setStage] = useState<BlogStageInfo | null>(null);
  const [calendar, setCalendar] = useState<CalendarWeek[]>([]);
  const [posts, setPosts] = useState<ContentItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<ContentItem | null>(null);
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());
  const [editingWeek, setEditingWeek] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isApproving, setIsApproving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [filterSeason, setFilterSeason] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [showRevisionForm, setShowRevisionForm] = useState(false);
  const [revisionFeedback, setRevisionFeedback] = useState('');
  const [isRevising, setIsRevising] = useState(false);
  const [revisionSubmitted, setRevisionSubmitted] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const stageInfo = await api.getBlogStage();
      setStage(stageInfo);

      if (stageInfo.stage === 'calendar' || stageInfo.stage === 'content_ready') {
        const review = await api.getPhaseReview('blog');
        const calendarItem = review.content_items.find(i => i.content_type.startsWith('blog_calendar'));
        if (calendarItem) {
          const full = await api.getContentItem(calendarItem.id);
          try {
            const parsed = JSON.parse(full.content);
            setCalendar(parsed);
            const months = new Set<string>(parsed.map((w: CalendarWeek) => w.month));
            setExpandedMonths(months);
          } catch {}
        }
      } else if (stageInfo.stage === 'content' || stageInfo.stage === 'completed') {
        const review = await api.getPhaseReview('blog');
        const blogPosts = review.content_items.filter(i => i.content_type === 'blog_post');
        setPosts(blogPosts);
        const calendarItem = review.content_items.find(i => i.content_type.startsWith('blog_calendar'));
        if (calendarItem) {
          const full = await api.getContentItem(calendarItem.id);
          try { setCalendar(JSON.parse(full.content)); } catch {}
        }
      }
    } catch (error) {
      console.error('Failed to load blog data:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Group calendar by month
  const filteredCalendar = calendar.filter(w => {
    if (filterSeason !== 'all' && w.season !== filterSeason) return false;
    if (filterType !== 'all' && w.blog.type !== filterType) return false;
    return true;
  });
  const filteredMonthGroups: Record<string, CalendarWeek[]> = {};
  for (const week of filteredCalendar) {
    if (!filteredMonthGroups[week.month]) filteredMonthGroups[week.month] = [];
    filteredMonthGroups[week.month].push(week);
  }
  const sortedFilteredMonths = MONTH_ORDER.filter(m => m in filteredMonthGroups);

  const typeCounts: Record<string, number> = {};
  for (const w of calendar) {
    typeCounts[w.blog.type] = (typeCounts[w.blog.type] || 0) + 1;
  }

  // Group posts by month using calendar data
  const parsePostWeek = (key: string | undefined): number => {
    if (!key) return 0;
    const n = parseInt(key.replace(/^blog_(week|post)_/, ''));
    return isNaN(n) ? 0 : n;
  };
  const sortedPosts = [...posts].sort((a, b) => parsePostWeek(a.content_key) - parsePostWeek(b.content_key));
  const postsByMonth: Record<string, ContentItem[]> = {};
  for (const post of sortedPosts) {
    const weekNum = parsePostWeek(post.content_key);
    const calWeek = calendar.find(w => w.week_number === weekNum);
    const month = calWeek?.month || 'Posts';
    if (!postsByMonth[month]) postsByMonth[month] = [];
    postsByMonth[month].push(post);
  }
  const sortedPostMonths = [
    ...MONTH_ORDER.filter(m => m in postsByMonth),
    ...Object.keys(postsByMonth).filter(m => !MONTH_ORDER.includes(m)),
  ];

  const toggleMonth = (month: string) => {
    setExpandedMonths(prev => {
      const next = new Set(prev);
      if (next.has(month)) next.delete(month); else next.add(month);
      return next;
    });
  };

  const startEditTitle = (week: CalendarWeek) => {
    setEditingWeek(week.week_number);
    setEditTitle(week.blog.title);
  };

  const saveTitle = async () => {
    if (editingWeek === null || !editTitle.trim()) return;
    try {
      await api.updateBlogCalendarTitles([{ week_number: editingWeek, title: editTitle.trim() }]);
      setCalendar(prev => prev.map(w =>
        w.week_number === editingWeek ? { ...w, blog: { ...w.blog, title: editTitle.trim() } } : w
      ));
    } catch (error) { console.error('Failed to save title:', error); }
    setEditingWeek(null);
  };

  const handleApproveCalendar = async () => {
    setIsApproving(true);
    try {
      await api.approvePhaseStage('blog', 'calendar');
      setStage({ stage: 'content_ready' });
    } catch (error) { console.error('Failed to approve calendar:', error); }
    finally { setIsApproving(false); }
  };

  const handleGeneratePosts = async () => {
    setIsGenerating(true);
    try {
      await api.startPhase('blog', { stage: 'content' });
      setIsLoading(true);
      await loadData();
    } catch (error) { console.error('Failed to generate posts:', error); }
    finally { setIsGenerating(false); }
  };

  const handleApproveFinal = async () => {
    setIsApproving(true);
    try {
      await api.approvePhase('blog');
      navigate('/');
    } catch (error) { console.error('Failed to approve:', error); }
    finally { setIsApproving(false); }
  };

  const handleRequestRevision = async () => {
    if (!revisionFeedback.trim()) return;
    setIsRevising(true);
    try {
      await api.requestRevision('blog', revisionFeedback);
      setRevisionSubmitted(true);
      setTimeout(() => navigate('/'), 3500);
      setShowRevisionForm(false);
      setRevisionFeedback('');
      navigate('/');
    } catch (error) { console.error('Failed to request revision:', error); } finally { setIsRevising(false); }
  };

  const handleViewPost = async (item: ContentItem) => {
    try {
      const fullItem = await api.getContentItem(item.id);
      setSelectedItem(fullItem);
    } catch (error) { console.error('Failed to load post:', error); }
  };


  if (revisionSubmitted) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-6">
        <div className="bg-sage-50 border border-sage-200 rounded-2xl p-10 max-w-md w-full shadow-sm">
          <div className="text-4xl mb-4">🌲</div>
          <h2 className="text-xl font-bold text-charcoal-500 mb-2">The Rangers are on it.</h2>
          <p className="text-charcoal-300 text-sm leading-relaxed">Your revision request has been submitted. The Rangers are already working on your updates — head back to your dashboard to track progress.</p>
          <p className="text-xs text-charcoal-200 mt-4">Returning to dashboard…</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-copper-500"></div>
      </div>
    );
  }

  if (isGenerating) {
    return (
      <div className="max-w-lg mx-auto text-center py-16 space-y-6">
        <div className="relative inline-flex items-center justify-center w-20 h-20 rounded-full bg-copper-100 mx-auto">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-copper-200 border-t-copper-500"></div>
          <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle,rgba(196,117,76,0.15),transparent_70%)]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-charcoal-500">Writing 52 Blog Posts</h1>
          <p className="text-charcoal-300 mt-2">This takes a few minutes. We're generating all your posts in batches...</p>
        </div>
        <p className="text-sm text-charcoal-300">You can wait here or close this page — we'll email you when they're ready.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <Link to="/phases/blog" className="inline-flex items-center text-charcoal-300 hover:text-copper-600 transition-colors text-sm mb-6">
        <ArrowLeftIcon className="h-4 w-4 mr-1.5" />
        Back to phase
      </Link>

      {/* ====== CALENDAR REVIEW VIEW ====== */}
      {(stage?.stage === 'calendar' || stage?.stage === 'content_ready') && (
        <div className="space-y-6">
          {/* Header card */}
          <div className="relative overflow-hidden bg-white rounded-2xl border border-charcoal-100 shadow-sm">
            {/* Decorative background */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute -top-16 -right-16 w-48 h-48 bg-[radial-gradient(circle,rgba(196,117,76,0.05),transparent_70%)]" />
              <svg className="absolute right-4 top-4 w-20 h-20 opacity-[0.03]" viewBox="0 0 80 80">
                <circle cx="60" cy="20" r="35" fill="none" stroke="#2C3E3D" strokeWidth="1" />
                <circle cx="60" cy="20" r="20" fill="none" stroke="#2C3E3D" strokeWidth="0.7" />
              </svg>
            </div>
            <div className="h-1 bg-gradient-to-r from-copper-400 via-sage-400 to-teal-400" />
            <div className="relative p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-xl bg-copper-50">
                  <CalendarDaysIcon className="h-6 w-6 text-copper-500" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-charcoal-500">Your 52-Week Blog Calendar</h1>
                  <p className="text-charcoal-300 mt-1">Review your blog plan. Click any title to edit it. When you're happy, approve to generate full posts.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Stats bar */}
          <div className="relative overflow-hidden bg-white rounded-2xl border border-charcoal-100 shadow-sm p-4">
            <div className="flex flex-wrap gap-2 items-center">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-charcoal-50 text-charcoal-500 text-sm font-medium">
                <SparklesIcon className="h-4 w-4 text-copper-400" />
                {calendar.length} weeks
              </span>
              {Object.entries(typeCounts).sort().map(([type, count]) => {
                const ts = TYPE_STYLES[type] || { bg: 'bg-charcoal-50', text: 'text-charcoal-400' };
                return (
                  <span key={type} className={`inline-block px-3 py-1.5 rounded-full text-sm font-medium ${ts.bg} ${ts.text}`}>
                    {type.replace('-', ' ')}: {count}
                  </span>
                );
              })}
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            <select value={filterSeason} onChange={(e) => setFilterSeason(e.target.value)} className="input !w-auto !rounded-xl">
              <option value="all">All Seasons</option>
              <option value="winter">Winter</option>
              <option value="spring">Spring</option>
              <option value="summer">Summer</option>
              <option value="fall">Fall</option>
            </select>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="input !w-auto !rounded-xl">
              <option value="all">All Types</option>
              {Object.keys(TYPE_STYLES).map(t => (
                <option key={t} value={t}>{t.replace('-', ' ')}</option>
              ))}
            </select>
          </div>

          {/* Monthly accordions */}
          <div className="space-y-3">
            {sortedFilteredMonths.map((month) => {
              const weeks = filteredMonthGroups[month];
              const season = weeks[0].season;
              const ss = SEASON_STYLES[season] || SEASON_STYLES.winter;
              const isOpen = expandedMonths.has(month);

              return (
                <div key={month} className={`relative overflow-hidden bg-white rounded-2xl border border-charcoal-100 shadow-sm border-l-4 ${ss.border}`}>
                  <button
                    onClick={() => toggleMonth(month)}
                    className={`w-full text-left px-5 py-4 flex items-center justify-between ${ss.bg} hover:opacity-90 transition-opacity`}
                  >
                    <span className={`font-bold ${ss.text}`}>{month}</span>
                    <div className="flex items-center gap-3">
                      <span className={`text-sm ${ss.text} opacity-75`}>{weeks.length} posts &middot; {ss.label}</span>
                      {isOpen ? <ChevronUpIcon className={`h-5 w-5 ${ss.text}`} /> : <ChevronDownIcon className={`h-5 w-5 ${ss.text}`} />}
                    </div>
                  </button>

                  {isOpen && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-cream-50 text-left text-charcoal-300">
                            <th className="px-5 py-2.5 w-12 text-xs uppercase tracking-wide font-medium">Wk</th>
                            <th className="px-5 py-2.5 w-24 text-xs uppercase tracking-wide font-medium">Dates</th>
                            <th className="px-5 py-2.5 text-xs uppercase tracking-wide font-medium">Title</th>
                            <th className="px-5 py-2.5 w-24 text-xs uppercase tracking-wide font-medium">Type</th>
                            <th className="px-5 py-2.5 w-32 hidden sm:table-cell text-xs uppercase tracking-wide font-medium">Keyword</th>
                          </tr>
                        </thead>
                        <tbody>
                          {weeks.map((week) => {
                            const ts = TYPE_STYLES[week.blog.type] || { bg: 'bg-charcoal-50', text: 'text-charcoal-400' };
                            const dates = `${week.start_date.slice(5)} – ${week.end_date.slice(5)}`;
                            const isEditing = editingWeek === week.week_number;
                            return (
                              <tr key={week.week_number} className="border-b border-charcoal-50 hover:bg-cream-50 transition-colors">
                                <td className="px-5 py-2.5 text-charcoal-250 font-medium">{week.week_number}</td>
                                <td className="px-5 py-2.5 text-charcoal-250 text-xs whitespace-nowrap">{dates}</td>
                                <td className="px-5 py-2.5">
                                  {isEditing ? (
                                    <input
                                      type="text" value={editTitle}
                                      onChange={(e) => setEditTitle(e.target.value)}
                                      onBlur={saveTitle}
                                      onKeyDown={(e) => { if (e.key === 'Enter') saveTitle(); if (e.key === 'Escape') setEditingWeek(null); }}
                                      className="input !py-1 !text-sm w-full !rounded-lg" autoFocus
                                    />
                                  ) : (
                                    <button onClick={() => startEditTitle(week)}
                                      className="text-left font-medium text-charcoal-500 hover:text-copper-600 transition-colors group w-full" title="Click to edit">
                                      {week.blog.title}
                                      <PencilSquareIcon className="h-3.5 w-3.5 inline ml-1 opacity-0 group-hover:opacity-50 transition-opacity" />
                                    </button>
                                  )}
                                </td>
                                <td className="px-5 py-2.5">
                                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${ts.bg} ${ts.text}`}>
                                    {week.blog.type.replace('-', ' ')}
                                  </span>
                                </td>
                                <td className="px-5 py-2.5 text-charcoal-300 text-xs hidden sm:table-cell">{week.blog.target_keyword}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Calendar actions */}
          {!showRevisionForm && (
            <div className="relative overflow-hidden bg-white rounded-2xl border border-charcoal-100 shadow-sm p-6 space-y-3">
              {stage?.stage === 'calendar' && (
                <>
                  <button onClick={handleApproveCalendar} disabled={isApproving}
                    className="btn btn-primary w-full flex items-center justify-center disabled:opacity-50 shadow-sm shadow-copper-500/20">
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    {isApproving ? 'Approving...' : 'Approve Calendar & Generate Posts'}
                  </button>
                  <button onClick={() => setShowRevisionForm(true)}
                    className="btn btn-outline w-full flex items-center justify-center">
                    <PencilSquareIcon className="h-5 w-5 mr-2" />
                    Request Revisions
                  </button>
                </>
              )}
              {stage?.stage === 'content_ready' && (
                <button onClick={handleGeneratePosts}
                  className="btn btn-primary w-full flex items-center justify-center shadow-sm shadow-copper-500/20">
                  <PlayIcon className="h-5 w-5 mr-2" />
                  Generate All 52 Blog Posts
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* ====== POST REVIEW VIEW — SIDEBAR LAYOUT ====== */}
      {(stage?.stage === 'content' || stage?.stage === 'completed') && (
        <>
          {/* Header card */}
          <div className="relative overflow-hidden bg-white rounded-2xl border border-charcoal-100 shadow-sm mb-6">
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute -top-16 -right-16 w-48 h-48 bg-[radial-gradient(circle,rgba(90,123,111,0.05),transparent_70%)]" />
            </div>
            <div className="h-1 bg-gradient-to-r from-sage-400 via-teal-400 to-copper-400" />
            <div className="relative p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-sage-50">
                  <DocumentTextIcon className="h-6 w-6 text-sage-500" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-charcoal-500">Review Your Blog Posts</h1>
                  <p className="text-charcoal-300 mt-1">{posts.length} posts generated. Select any post to read and review.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile: Dropdown selector */}
          <div className="lg:hidden mb-4">
            <select
              onChange={(e) => {
                const post = posts.find(p => p.id === e.target.value);
                if (post) handleViewPost(post);
              }}
              value={selectedItem?.id || ''}
              className="input w-full !rounded-xl"
            >
              <option value="">Select a post to view...</option>
              {sortedPostMonths.map(month => (
                <optgroup key={month} label={month}>
                  {postsByMonth[month].map(post => (
                    <option key={post.id} value={post.id}>{post.title}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Sidebar — Post List */}
            <div className="hidden lg:block lg:col-span-1 space-y-4">
              <div className="relative overflow-hidden bg-white rounded-2xl border border-charcoal-100 shadow-sm max-h-[calc(100vh-280px)] overflow-y-auto">
                {sortedPostMonths.map((month) => {
                  const monthPosts = postsByMonth[month];
                  const firstWeekNum = parsePostWeek(monthPosts[0]?.content_key);
                  const calWeek = calendar.find(w => w.week_number === firstWeekNum);
                  const season = calWeek?.season || 'winter';
                  const ss = SEASON_STYLES[season];

                  return (
                    <div key={month}>
                      <div className={`px-4 py-2.5 ${ss.bg} border-b border-charcoal-50 sticky top-0 z-10`}>
                        <span className={`text-xs font-semibold uppercase tracking-wider ${ss.text}`}>{month}</span>
                        <span className={`text-xs ml-2 ${ss.text} opacity-60`}>{monthPosts.length} posts</span>
                      </div>
                      <div className="divide-y divide-charcoal-50">
                        {monthPosts.map(post => {
                          const isSelected = selectedItem?.id === post.id;
                          const weekNum = parsePostWeek(post.content_key);
                          const cw = calendar.find(w => w.week_number === weekNum);
                          const postType = cw?.blog?.type;
                          const ts = postType ? (TYPE_STYLES[postType] || { bg: 'bg-charcoal-50', text: 'text-charcoal-400' }) : null;

                          return (
                            <button
                              key={post.id}
                              onClick={() => handleViewPost(post)}
                              className={`w-full text-left px-4 py-3 transition-colors ${
                                isSelected ? 'bg-copper-50 border-l-2 border-copper-500' : 'hover:bg-cream-50 border-l-2 border-transparent'
                              }`}
                            >
                              <p className={`text-sm leading-snug ${isSelected ? 'font-semibold text-copper-700' : 'font-medium text-charcoal-500'}`}>
                                {post.title}
                              </p>
                              <div className="flex items-center gap-2 mt-1">
                                {weekNum > 0 && <span className="text-xs text-charcoal-250">Wk {weekNum}</span>}
                                {ts && (
                                  <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${ts.bg} ${ts.text}`}>
                                    {postType?.replace('-', ' ')}
                                  </span>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Actions */}
              {!showRevisionForm && stage?.stage !== 'completed' && (
                <div className="relative overflow-hidden bg-white rounded-2xl border border-charcoal-100 shadow-sm p-5 space-y-3">
                  <button onClick={handleApproveFinal} disabled={isApproving}
                    className="btn btn-primary w-full flex items-center justify-center disabled:opacity-50 shadow-sm shadow-copper-500/20">
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    {isApproving ? 'Approving...' : 'Approve & Continue'}
                  </button>
                  <button onClick={() => setShowRevisionForm(true)}
                    className="btn btn-outline w-full flex items-center justify-center">
                    <PencilSquareIcon className="h-5 w-5 mr-2" />
                    Request Revisions
                  </button>
                </div>
              )}
            </div>

            {/* Content Preview */}
            <div className="lg:col-span-2">
              {showRevisionForm ? (
                <div className="relative overflow-hidden bg-white rounded-2xl border border-charcoal-100 shadow-sm p-6">
                  <h2 className="text-lg font-bold text-charcoal-500 mb-3">Request Revisions</h2>
                  <p className="text-charcoal-300 text-sm mb-4">Tell us what you'd like changed:</p>
                  <textarea
                    value={revisionFeedback}
                    onChange={(e) => setRevisionFeedback(e.target.value)}
                    rows={4} className="input mb-4"
                    placeholder="Example: I'd like more how-to posts in Q2, and fewer listicles..."
                  />
                  <div className="flex gap-3">
                    <button onClick={handleRequestRevision} disabled={!revisionFeedback.trim() || isRevising} className="btn btn-primary disabled:opacity-50">{isRevising ? 'Updating...' : 'Submit'}</button>
                    <button onClick={() => setShowRevisionForm(false)} className="btn btn-secondary">Cancel</button>
                  </div>
                </div>
              ) : selectedItem ? (
                <div className="relative overflow-hidden bg-white rounded-2xl border border-charcoal-100 shadow-sm">
                  {/* Decorative elements */}
                  <div className="absolute inset-0 pointer-events-none">
                    <svg className="absolute right-4 top-4 w-20 h-20 opacity-[0.03]" viewBox="0 0 80 80">
                      <circle cx="60" cy="20" r="35" fill="none" stroke="#2C3E3D" strokeWidth="1" />
                      <circle cx="60" cy="20" r="20" fill="none" stroke="#2C3E3D" strokeWidth="0.7" />
                    </svg>
                  </div>
                  <div className="relative p-6">
                    <div className="mb-6">
                      <h2 className="text-2xl font-bold text-charcoal-500">{selectedItem.title}</h2>
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-charcoal-50 text-charcoal-500">blog post</span>
                        {selectedItem.metadata?.target_keyword && (
                          <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-600">{selectedItem.metadata.target_keyword}</span>
                        )}
                        {selectedItem.metadata?.meta_description && (
                          <p className="w-full text-xs text-charcoal-300 mt-2 italic">{selectedItem.metadata.meta_description}</p>
                        )}
                      </div>
                    </div>
                    {selectedItem.content_html ? (
                      <div
                        className="prose prose-sm max-w-none [&_blockquote]:border-l-4 [&_blockquote]:border-copper-400 [&_blockquote]:bg-copper-50 [&_blockquote]:pl-4 [&_blockquote]:py-2 [&_blockquote]:my-4 [&_blockquote]:rounded-r-lg [&_blockquote]:italic"
                        dangerouslySetInnerHTML={{ __html: selectedItem.content_html || selectedItem.content || '' }}
                      />
                    ) : (
                      <pre className="whitespace-pre-wrap text-sm text-charcoal-400 bg-cream-50 p-4 rounded-xl overflow-auto max-h-96">
                        {selectedItem.content}
                      </pre>
                    )}
                  </div>
                </div>
              ) : (
                <div className="relative overflow-hidden bg-white rounded-2xl border border-charcoal-100 shadow-sm text-center py-16">
                  <EyeIcon className="h-12 w-12 text-charcoal-200 mx-auto mb-4" />
                  <p className="text-charcoal-300">Select a blog post to preview</p>
                </div>
              )}
            </div>
          </div>

          {/* Mobile: Approve/Revisions at bottom */}
          {!showRevisionForm && stage?.stage !== 'completed' && (
            <div className="lg:hidden mt-6 space-y-3">
              <button onClick={handleApproveFinal} disabled={isApproving}
                className="btn btn-primary w-full flex items-center justify-center disabled:opacity-50 shadow-sm shadow-copper-500/20">
                <CheckCircleIcon className="h-5 w-5 mr-2" />
                {isApproving ? 'Approving...' : 'Approve & Continue'}
              </button>
              <button onClick={() => setShowRevisionForm(true)}
                className="btn btn-outline w-full flex items-center justify-center">
                <PencilSquareIcon className="h-5 w-5 mr-2" />
                Request Revisions
              </button>
            </div>
          )}
        </>
      )}

      {/* ====== NOT STARTED ====== */}
      {stage?.stage === 'not_started' && (
        <div className="relative overflow-hidden bg-white rounded-2xl border border-charcoal-100 shadow-sm text-center py-12">
          <p className="text-charcoal-300">Blog calendar hasn't been generated yet.</p>
          <Link to="/phases/blog" className="text-copper-600 text-sm mt-2 hover:underline">Go to Phase 7 to start</Link>
        </div>
      )}

      {/* ====== REVISION FORM (shared, for calendar stage) ====== */}
      {showRevisionForm && (stage?.stage === 'calendar' || stage?.stage === 'content_ready') && (
        <div className="relative overflow-hidden bg-white rounded-2xl border border-charcoal-100 shadow-sm p-6 mt-6">
          <h2 className="text-lg font-bold text-charcoal-500 mb-3">Request Revisions</h2>
          <p className="text-charcoal-300 text-sm mb-4">Tell us what you'd like changed:</p>
          <textarea
            value={revisionFeedback}
            onChange={(e) => setRevisionFeedback(e.target.value)}
            rows={4} className="input mb-4"
            placeholder="Example: I'd like more how-to posts in Q2, and fewer listicles..."
          />
          <div className="flex gap-3">
            <button onClick={handleRequestRevision} disabled={!revisionFeedback.trim() || isRevising} className="btn btn-primary disabled:opacity-50">{isRevising ? 'Updating...' : 'Submit'}</button>
            <button onClick={() => setShowRevisionForm(false)} className="btn btn-secondary">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
